// The `evidence/library` source-list READ hooks — carved from
// `src/components/library/source-lists/hooks/useSourceLists.ts` (now a bodied
// delegate for these three names) at D2 slice 2.
//
// THIS IS THE SLICE THE FAMILY RECORD SIZED AND THEN DELIBERATELY SKIPPED.
// The `query` note has said since ARCFE-1088 that all three reads are blocked
// by exactly one thing each, the SAME thing — a
// `useSelector((state: RootState) => state.auth.token)` composed into `enabled`
// beside a flag getter — and that the heal is the one-boolean `enabled`
// severance `calls/conversations` ships. ARCFE-1088 left it out on purpose
// ("bundling a zero-cost move with a behavioural seam hides the half a
// reviewer needs to look at"). This is that behavioural seam, on its own.
//
// S-enabled — THE SEVERANCE, and it is one parameter rather than two. The
// legacy gate was `!!token && IS_EVIDENCE_LIBRARY_ENABLED()` (or
// `IS_UNIFIED_COLLECTIONS_ENABLED()` on the unmigrated read). Both terms are
// app facts: one is the Rematch auth store, the other a flag getter, and
// neither may enter a headless zone. They collapse into a single
// caller-computed boolean, so the package never learns that a flag key or an
// auth store exists — the `isLiveRefreshEnabled` shape, not a two-field
// options object that would re-teach the package the legacy structure.
//
// WHAT STAYS PACKAGE-SIDE, and the distinction is the reusable part:
// `useSourceList`'s `!!sourceListId` term is about the ARGUMENT, not about the
// app, so it is `&&`-ed in here. Hoisting it to the delegate would let a caller
// pass `enabled: true` with a null id and fire a request at `/source-lists/`
// with an undefined segment. The two terms are combined in the same order the
// legacy expression used, which matters only for short-circuit evaluation and
// costs nothing to preserve.
//
// THE ERROR-SWALLOWING IS FROZEN BEHAVIOUR, NOT A DEFECT TO TIDY. Each read
// catches, logs and returns an EMPTY projection (`[]`, or `undefined` for the
// detail read) rather than rethrowing, so every consumer sees `isSuccess` with
// empty data instead of `isError`. Callers depend on it: `LibraryPage` renders
// its empty state off `data`, and the chat source pickers treat "no lists" and
// "lists unavailable" identically by design. A rethrow here would surface an
// error state ~40 call sites have never had to render.
//
// `logger` crosses as the SAME SINK rather than as a port:
// `@/utils/logger/logger` is a pure runtime re-export of
// `@heidi/runtime/logger` (Wave 1 · P1), so importing the real module here is
// not a severance at all — the calls-websocket precedent (ARCFE-1053).
//
// THE DELEGATE IS GONE AND THE GATE CAME WITH IT (the no-shim ruling,
// 2026-10-08). `src/components/library/source-lists/hooks/useSourceLists.ts`
// is deleted, so there is no app-side body left to compute `isAuthenticated &&
// <flag>()`. Both terms now arrive through `configureEvidenceData` — the
// verdict as a hook (ruling 65), the flag as a thunk — and each hook below
// computes the legacy expression itself, term for term and in the legacy
// order. `enabled` stays on the options object as an OPTIONAL override so the
// package suite can still drive the gate directly; when it is omitted the
// hooks resolve it, which is what every call site now does.
//
// THE INJECTED HOOK IS CALLED UNCONDITIONALLY, before the `??`. Resolving it
// lazily — only when the caller passed nothing — would make a hook call
// conditional on an argument, which is the rules-of-hooks violation a
// `??`-shaped default invites.
import { useQuery } from '@tanstack/react-query';

import { logger } from '@heidi/runtime/logger';

import {
  SOURCE_LISTS_QUERY_KEY,
  SOURCE_LIST_DETAIL_QUERY_KEY,
  UNMIGRATED_SOURCE_LISTS_QUERY_KEY,
  type SourceList,
} from '../../contracts';
import { getSourceList, listSourceLists, listUnmigratedSourceLists } from '../api';
import {
  isEvidenceLibraryEnabled,
  isUnifiedCollectionsEnabled,
  useIsAuthenticated,
} from '../injected';

export interface SourceListReadOptions {
  /**
   * Caller override for the gate. OMIT IT and the hook computes the app's
   * own `isAuthenticated && <flag>()`, which is what every call site does;
   * pass it and that answer is used instead.
   *
   * `undefined` IS PART OF THE CONTRACT AND IS NOT A MISSING BOOLEAN — this is
   * the one place the severance could have shipped a silent behaviour change,
   * so it is typed rather than coerced. `IS_EVIDENCE_LIBRARY_ENABLED()` is
   * `posthog.isFeatureEnabled(…) && …`, which returns `undefined` while the
   * PostHog client is still loading, and the legacy expression
   * `!!token && isLibraryEnabled` therefore evaluated to `undefined` for a
   * signed-in user in that window — which react-query reads as its DEFAULT,
   * i.e. ENABLED. Tightening this to `boolean` and writing `!!isLibraryEnabled`
   * would suppress a request the app makes today, on every cold load, for as
   * long as the flag client takes to resolve. So it crosses verbatim and
   * react-query applies the same default it always did.
   *
   * `undefined` therefore cannot mean "not supplied" here. The default is
   * resolved by the ABSENCE OF THE KEY (`'enabled' in options`), never by
   * `?? computed`, which would turn the live tri-state into the default.
   */
  enabled?: boolean | undefined;
}

/** The library gate: `isAuthenticated && IS_EVIDENCE_LIBRARY_ENABLED()`. */
const useLibraryGate = (options: SourceListReadOptions): boolean | undefined => {
  const isLibraryEnabled = isEvidenceLibraryEnabled();
  const isAuthenticated = useIsAuthenticated();
  return 'enabled' in options ? options.enabled : isAuthenticated && isLibraryEnabled;
};

/**
 * Hook to fetch all source lists for the current user
 */
export function useSourceLists(options: SourceListReadOptions = {}) {
  const enabled = useLibraryGate(options);
  return useQuery<SourceList[]>({
    queryKey: [SOURCE_LISTS_QUERY_KEY],
    queryFn: async () => {
      try {
        return await listSourceLists();
      } catch (error) {
        logger.error('Failed to fetch source lists:', error);
        return [];
      }
    },
    enabled,
  });
}

/**
 * Hook to fetch a single source list
 */
export function useSourceList(
  sourceListId: string | null,
  options: SourceListReadOptions = {}
) {
  const enabled = useLibraryGate(options);
  return useQuery<SourceList | undefined>({
    queryKey: [SOURCE_LIST_DETAIL_QUERY_KEY, sourceListId],
    queryFn: async () => {
      try {
        return await getSourceList(sourceListId!);
      } catch (error) {
        logger.error('Failed to fetch source list:', error);
        return undefined;
      }
    },
    // THE ID TERM COMES FIRST, AND THE ORDER IS LOAD-BEARING RATHER THAN
    // STYLISTIC. `enabled` may be `undefined` (see the options doc), and
    // `undefined && false` is `undefined` — which react-query reads as ENABLED.
    // Written the other way round, a null id during the flag-loading window
    // would fire a GET at `/source-lists/null`. `!!sourceListId && enabled`
    // short-circuits to a hard `false` on a null id and otherwise passes
    // `enabled` through untouched, which is the legacy
    // `!!token && !!sourceListId && isLibraryEnabled` result on every input.
    enabled: !!sourceListId && enabled,
  });
}

/**
 * Fetch only unmigrated source lists (not yet converted to collections).
 */
export function useUnmigratedSourceLists(options: SourceListReadOptions = {}) {
  // A DIFFERENT FLAG, deliberately. The unmigrated read answers to
  // `IS_UNIFIED_COLLECTIONS_ENABLED()`, not the library flag: the two are
  // independent rollouts and merging them would tie them together with
  // nothing at any layer saying so.
  const isUnifiedEnabled = isUnifiedCollectionsEnabled();
  const isAuthenticated = useIsAuthenticated();
  const enabled =
    'enabled' in options ? options.enabled : isAuthenticated && isUnifiedEnabled;
  return useQuery<SourceList[]>({
    queryKey: [UNMIGRATED_SOURCE_LISTS_QUERY_KEY],
    queryFn: async () => {
      try {
        return await listUnmigratedSourceLists();
      } catch (error) {
        logger.error('Failed to fetch unmigrated source lists:', error);
        return [];
      }
    },
    enabled,
  });
}
